import { useLiveQuery } from 'dexie-react-hooks'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNetworkStatus } from '@/api'
import { useAuth } from '@/auth/context'
import { usePhoneSession } from '@/auth/pin/phoneSession'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { DemoDeviceError, downloadCaches, roster, useDeviceInfo, useSyncStatus } from '@/offline'
import { useToast } from '@/ui'
import { env } from '@/lib/env'
import { useTheme } from '@/app/theme'
import { useInstallPrompt } from '@/app/pwa/useInstallPrompt'
import {
  Avatar,
  Btn,
  Card,
  GeneralHelpSheet,
  Row,
  Strip,
  V1Header,
  V1Screen,
} from '@/features/gate/v1/kit'
import { WeighHelpSheet } from '@/features/gate/v1/delivery/Tag'
import { C } from '@/features/gate/v1/style'
import { listWords } from '../listWords'
import { downloadExport } from '@/offline/exportData'

/** A role in words ("village_operator" → "ख़रीद ऑपरेटर"), else the role code made readable. */
function roleName(t: (k: MessageKey) => string, role: string): string {
  const key = `role.${role}` as MessageKey
  const word = t(key)
  return word === key ? role.replace(/_/g, ' ') : word
}

/** More, in the alignment map's look: who is working, this phone, language and look, the account. */
export function MorePage() {
  const { t, locale, setLocale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const navigate = useNavigate()
  const sync = useSyncStatus()
  const { online } = useNetworkStatus()
  const { actor, lock } = usePhoneSession()
  const { status, user, logout } = useAuth()
  const info = useDeviceInfo()
  const words = listWords(info.registration?.team.kind)
  const [theme, setTheme] = useTheme()
  const { canInstall, install } = useInstallPrompt()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Help moved here from the screen header (the header now carries the update chip).
  const [help, setHelp] = useState<null | 'general' | 'weigh'>(null)
  const [exporting, setExporting] = useState(false)

  const exportData = async () => {
    setExporting(true)
    try {
      const r = await downloadExport()
      toast.success(
        t('more.exported', { records: r.records, drafts: r.drafts, photos: r.photos }),
        r.photosUploadedOnly
          ? t('more.exportedUploaded', { count: r.photosUploadedOnly })
          : undefined,
      )
    } catch (e) {
      // The share sheet closed without sharing is not a failure.
      if (!(e instanceof DOMException && e.name === 'AbortError'))
        toast.error(t('more.exportFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setExporting(false)
    }
  }
  const signedIn = status === 'authenticated'
  // Multi-village phones: this person works in more than one village.
  const member = useLiveQuery(
    async () => (actor ? await roster.teamMember(actor.user_id) : undefined),
    [actor?.user_id],
  )
  const manyVillages =
    info.registration?.team.kind === 'VILLAGE' && (member?.villages?.length ?? 0) > 1

  const update = async () => {
    setBusy(true)
    setError(null)
    try {
      await downloadCaches()
      toast.success(t(words.updated))
    } catch (e) {
      setError(
        e instanceof DemoDeviceError
          ? t('device.demoOffline')
          : e instanceof Error
            ? e.message
            : String(e),
      )
    } finally {
      setBusy(false)
    }
  }

  const facts: [string, string, string?][] = [
    [t('device.team'), info.registration?.team.name ?? '—'],
    [t('device.prefix'), info.registration?.code_prefix ?? '—'],
    [t('device.uid'), info.deviceUid?.slice(-12) ?? '—'],
    [
      t(words.label),
      info.rosterDownloadedAt ? fmt.relative(info.rosterDownloadedAt) : t(words.never),
      info.rosterVersion ?? undefined,
    ],
  ]

  return (
    <V1Screen
      nav
      header={<V1Header pending={sync.pending} updatable icon="more" title={t('nav.more')} />}
    >
      <Card gap={10}>
        <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.2 }}>{t('pin.who')}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar size={52} icon={44} label={actor?.name} />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <b style={{ fontSize: 20, lineHeight: 1.25 }}>{actor?.name}</b>
            <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.3 }}>
              {actor?.roles.map((r) => roleName(t, r)).join(' · ')}
            </span>
          </span>
        </div>
        <Row>
          <Btn
            icon="user"
            onClick={() => {
              lock()
              navigate('/m/unlock')
            }}
          >
            {t('pin.switchUser')}
          </Btn>
          <Btn icon="lock" onClick={lock}>
            {t('pin.lock')}
          </Btn>
        </Row>
      </Card>

      <Card gap={0} padding="4px 12px 12px">
        <h2 style={{ margin: '6px 0 4px', fontSize: 18, lineHeight: 1.25 }}>{t('device.title')}</h2>
        {facts.map(([label, value, hint]) => (
          <div
            key={label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '8px 0',
              borderBottom: `1px solid ${C.rule}`,
            }}
          >
            <span style={{ fontSize: 16, color: C.muted }}>{label}</span>
            <span style={{ textAlign: 'right' }}>
              <b style={{ fontSize: 16 }}>{value}</b>
              {hint && (
                <span style={{ display: 'block', fontSize: 13, color: C.muted }}>v {hint}</span>
              )}
            </span>
          </div>
        ))}
        <div style={{ height: 10 }} />
        {error && <Strip icon="bang">{error}</Strip>}
        <Btn icon="download" disabled={!online || !signedIn || busy} onClick={() => void update()}>
          {t(words.update)}
        </Btn>
        {manyVillages && (
          <>
            <div style={{ height: 8 }} />
            <Btn
              icon="home"
              onClick={() => navigate('/m/gate/village', { state: { from: '/m/gate' } })}
            >
              {t('gate.v1.village.change')}
            </Btn>
          </>
        )}
        {!signedIn && (
          <span style={{ fontSize: 15, color: C.muted, marginTop: 6 }}>
            {t('sync.signInNeeded')}
          </span>
        )}
      </Card>

      <Card gap={10}>
        <h2 style={{ margin: 0, fontSize: 18, lineHeight: 1.25 }}>{t('gate.v1.help')}</h2>
        <Btn icon="info" onClick={() => setHelp('general')}>
          {t('gate.v1.help.generalTitle')}
        </Btn>
        <Btn icon="scale" onClick={() => setHelp('weigh')}>
          {t('gate.v1.more.weighHelp')}
        </Btn>
        <Btn icon="download" disabled={exporting} onClick={() => void exportData()}>
          {exporting ? t('more.exporting') : t('more.export')}
        </Btn>
      </Card>
      {help === 'general' && <GeneralHelpSheet onClose={() => setHelp(null)} />}
      {help === 'weigh' && <WeighHelpSheet onClose={() => setHelp(null)} />}

      <Card gap={12}>
        <Choice
          label={t('common.language')}
          value={locale}
          onChange={(l) => setLocale(l)}
          options={[
            ['hi', 'हिंदी'],
            ['en', 'English'],
          ]}
        />
        <Choice
          label={t('theme.label')}
          value={theme}
          onChange={setTheme}
          options={[
            ['light', t('theme.light')],
            ['dark', t('theme.dark')],
            ['system', t('theme.system')],
          ]}
        />
        {canInstall && (
          <Btn icon="download" onClick={() => void install()}>
            {t('pwa.installAction')}
          </Btn>
        )}
      </Card>

      {signedIn ? (
        <Btn icon="signout" onClick={() => void logout()}>
          {t('auth.signOut')} · {user?.username}
        </Btn>
      ) : (
        // Signed out (or the session ended): the account login sends work and updates lists.
        <Btn icon="user" onClick={() => navigate('/login?next=/m/more')}>
          {t('auth.signIn')}
        </Btn>
      )}
      <span style={{ textAlign: 'center', fontSize: 13, color: C.muted }}>v{env.appVersion}</span>
    </V1Screen>
  )
}

/** A row with a label and two or three answer chips (the boards' picked chip: ink border). */
function Choice<V extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: ReactNode
  value: V
  onChange: (v: V) => void
  options: [V, string][]
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25 }}>{label}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        {options.map(([v, text]) => {
          const picked = v === value
          return (
            <button
              key={v}
              type="button"
              aria-pressed={picked}
              onClick={() => onChange(v)}
              style={{
                flex: '1 1 0',
                minHeight: 48,
                borderRadius: 10,
                background: C.surface,
                color: C.ink,
                border: picked ? `3px solid ${C.ink}` : `1.5px solid ${C.border}`,
                fontFamily: 'inherit',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {text}
            </button>
          )
        })}
      </div>
    </div>
  )
}
